import React, { useState } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import { memberId } from '@trello/session-cookie';
import {
  useWorkspacesPreambleAddBoardToTeamMutation,
  useWorkspacesPreambleAddTeamMembersMutation,
  useWorkspacesPreambleSelectTeamQuery,
} from '@trello/graphql/generated';
import { WorkspacesPreambleBoardMemberList } from './WorkspacesPreambleBoardMemberList';
import styles from './WorkspacesPreambleSelectTeam.less';
import { Select } from '@trello/nachos';
import {
  Button,
  ButtonSizes,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import { UpgradePromptFull } from 'app/src/components/UpgradePrompts';
import { IconName } from 'app/common/components/controls/icon';
import { Feature } from 'app/scripts/debug/constants';
import Alerts from 'app/scripts/views/lib/alerts.coffee';
import { Spinner } from 'app/src/components/Spinner';
import { logError } from 'app/scripts/debug/log-error';
import { dontUpsell } from '@trello/config';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { SourceType } from '@trello/atlassian-analytics/src/constants/Source';

const noop = () => {};

const format = forNamespace('workspaces preamble');

interface WorkspacesPreambleSelectTeamProps {
  analyticsDirectObj: Noun;
  analyticsSource: SourceType;
  boardId: string;
  onChooseCreateTeam: () => void;
  onSuccess: () => void;
}

const CREATE_NEW_TEAM = 'CREATE_NEW_TEAM';

export const WorkspacesPreambleSelectTeam: React.FunctionComponent<WorkspacesPreambleSelectTeamProps> = ({
  analyticsDirectObj,
  analyticsSource,
  boardId,
  onChooseCreateTeam,
  onSuccess,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const { data } = useWorkspacesPreambleSelectTeamQuery({
    variables: { boardId },
  });

  const selectedTeam = data?.member?.organizations?.find((org) => {
    return org?.id === selectedTeamId;
  });
  const billingUrl = selectedTeam?.url && `${selectedTeam?.url}/billing`;
  const teamStatus = selectedTeam?.limits?.orgs?.freeBoardsPerOrg?.status;
  const showUpsellBanner =
    (teamStatus === 'disabled' || teamStatus === 'maxExceeded') && !dontUpsell;

  const [addBoardToTeam] = useWorkspacesPreambleAddBoardToTeamMutation();
  const [addBoardMembersToTeam] = useWorkspacesPreambleAddTeamMembersMutation();

  const teamSelectOptions: {
    label: string;
    value: string;
    isDisabled?: boolean;
    meta?: string;
  }[] = (data?.member?.organizations || []).map((org) => {
    const status = org?.limits?.orgs?.freeBoardsPerOrg?.status;
    const shouldDisable = !!(
      (status === 'disabled' || status === 'maxExceeded') &&
      dontUpsell
    );

    return {
      label: org.displayName,
      value: org.id,
      isDisabled: shouldDisable,
      meta: shouldDisable ? format('upgrade-prompt-headline') : undefined,
    };
  });
  teamSelectOptions.push({
    label: format('team-select-create-team-option'),
    value: CREATE_NEW_TEAM,
  });

  const onChange = (option: { label: string; value: string }) => {
    if (option.value === CREATE_NEW_TEAM) {
      onChooseCreateTeam();
    } else if (option.value) {
      setSelectedTeamId(option.value);
    }
  };

  const onSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();

    if (showUpsellBanner) {
      billingUrl && window.location.assign(billingUrl);
      return;
    }

    setIsSubmitted(true);
    try {
      await addBoardToTeam({
        variables: { boardId, orgId: selectedTeamId },
      });
      const usersToAddToTeam = selectedMembers.map((idMember) => ({
        id: idMember,
      }));
      const membersAddedCount = usersToAddToTeam.length;

      if (membersAddedCount) {
        await addBoardMembersToTeam({
          variables: {
            orgId: selectedTeamId,
            users: usersToAddToTeam,
          },
        });
      }

      const availableMembersCount = (data?.board?.members || []).filter(
        (member) => memberId !== member.id && member.confirmed,
      ).length;

      Analytics.sendTrackEvent({
        action: 'updated',
        actionSubject: 'organization',
        source: analyticsSource,
        containers: {
          board: {
            id: boardId,
          },
          organization: {
            id: selectedTeamId,
          },
        },
        attributes: {
          availableMembersCount,
          membersAddedCount,
          newOrg: false,
          updatedOn: 'board',
        },
      });
      trackUe({
        category: Noun.BOARD_VIEW,
        verb: Verb.SUBMITS,
        directObj: analyticsDirectObj,
        context: {
          availableMembersCount,
          boardId,
          membersAddedCount,
          newOrg: false,
          orgId: selectedTeamId,
        },
      });
      onSuccess();
    } catch (err) {
      Alerts.showLiteralText(err.message, 'error', 'alert', 5000);
      logError({
        error: err,
        tags: {
          ownershipArea: 'trello-bizteam',
          feature: Feature.WorkspacesPreamble,
        },
      });
      setIsSubmitted(false);
    }
  };

  const isButtonEnabled = selectedTeamId && !isSubmitted;

  return (
    <>
      <label htmlFor="workspacesPreambleTeamSelect">
        {format('team-select-label')}
      </label>
      <Select
        id="workspacesPreambleTeamSelect"
        defaultValue={{
          label: format('team-select-default-option'),
          value: '',
        }}
        isDisabled={isSubmitted}
        options={teamSelectOptions}
        onChange={onChange}
      />
      {selectedTeamId ? (
        <WorkspacesPreambleBoardMemberList
          boardId={boardId}
          onSelectMember={(currentSelectedMembers) =>
            setSelectedMembers(currentSelectedMembers)
          }
        />
      ) : null}
      {showUpsellBanner && (
        <UpgradePromptFull
          headline={format('upgrade-prompt-headline')}
          content={format('upgrade-prompt-content')}
          cta={format('upgrade-prompt-cta')}
          ctaLink={'/business-class'}
          iconName={IconName.Board}
          source="workspacesPreambleInlineDialog"
        />
      )}
      <Button
        className={styles.selectTeamSubmitButton}
        onClick={isButtonEnabled ? onSubmit : noop}
        size={ButtonSizes.FULLWIDTH}
        buttonType={
          isButtonEnabled ? ButtonTypes.PRIMARY : ButtonTypes.DISABLED
        }
      >
        {isSubmitted ? (
          <Spinner centered />
        ) : showUpsellBanner ? (
          format('submit-button-upgrade-team')
        ) : (
          format('prompt-continue-button-add-to-team')
        )}
      </Button>
    </>
  );
};
